/**
 * Error boundary for GitHubSettings.
 * Prevents a crash in the GitHub section from bricking the entire Settings panel.
 * The Reset button clears GitHub-related localStorage and re-renders clean.
 */

import { Component, type ReactNode } from 'react';
import { GitHubSettings } from './GitHubSettings';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

const GITHUB_STORAGE_KEYS = [
  'talkyn-github-project',
  'talkyn-github-user',
  'talkyn-github-token',
  'talkyn-github-install-status',
  'talkyn-github-app-slug',
] as const;

class GitHubSettingsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  handleReset = () => {
    for (const key of GITHUB_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '4px 0' }}>
          <div style={{
            fontSize: '11px',
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px',
          }}>
            GitHub
          </div>
          <div style={{
            padding: '12px',
            background: '#1a1a1a',
            border: '1px solid #442222',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#e44', fontSize: '13px', marginBottom: '8px' }}>
              GitHub settings encountered an error
            </div>
            <button
              onClick={this.handleReset}
              type="button"
              style={{
                padding: '6px 16px',
                border: '1px solid #444',
                borderRadius: '6px',
                background: 'transparent',
                color: '#ccc',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Reset
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * GitHubSettings wrapped in an error boundary.
 * Drop-in replacement for <GitHubSettings />.
 */
export function GitHubSettingsWithBoundary() {
  return (
    <GitHubSettingsErrorBoundary>
      <GitHubSettings />
    </GitHubSettingsErrorBoundary>
  );
}
